import type { GetPageResponse } from "@store/features/crm/pageV2Slice";
import type { StructuredExpression } from "skedyul-ui/ui";
import {
  buildKanbanPartitionKey,
  kanbanGroupKeysEqual,
  serializeKanbanPartitionKey,
  toKanbanGroupKey,
  UNDEFINED_KANBAN_GROUPING_KEY,
  type KanbanGroupKey,
  type KanbanPartitionKey,
} from "skedyul-core/features/crm/features/pages/features/blocks/resources/types.kanbanKeys";
import type {
  KanbanSectionConfig,
  KanbanSubsectionConfig,
} from "./types";
import { expandGroupedSections, expandGroupedSubsections } from "./sections";

export type KanbanPartitionMatch =
  | { field: string; kind: "eq"; eqValue: unknown }
  | { field: string; kind: "empty" }
  | { field: string; kind: "unmatched" };

export type KanbanPartitionSpec = {
  partition: KanbanPartitionKey;
  match: KanbanPartitionMatch[];
};

const isDerivedDateFieldExpr = (fieldExpr: Record<string, unknown>): boolean =>
  "day_of_week" in fieldExpr ||
  "hour_of_day" in fieldExpr ||
  "month_of_year" in fieldExpr ||
  "day_of_month" in fieldExpr;

const resolveValueFromFieldOptions = (
  pageData: GetPageResponse | undefined,
  fieldHandle: string,
  groupingKey: string,
): unknown | null => {
  const field = pageData?.fields?.find((f) => f.handle === fieldHandle);
  const options = field?.definition?.options;
  if (!options?.length) return null;
  const match = options.find((opt) => opt.label === groupingKey);
  return match ? match.value : null;
};

const extractEqMatchFromSubQuery = (
  subQuery: StructuredExpression | undefined,
  field: string,
  pageData: GetPageResponse | undefined,
  groupingKey: string,
): KanbanPartitionMatch | null => {
  if (groupingKey === UNDEFINED_KANBAN_GROUPING_KEY) {
    return { field, kind: "unmatched" };
  }

  if (subQuery && typeof subQuery === "object") {
    const record = subQuery as Record<string, unknown>;
    if ("NOT" in record) {
      return { field, kind: "unmatched" };
    }
    const fieldExpr = record[field];
    if (fieldExpr && typeof fieldExpr === "object") {
      const expr = fieldExpr as Record<string, unknown>;
      if ("isEmpty" in expr) {
        return { field, kind: "empty" };
      }
      if ("eq" in expr && expr.eq !== undefined) {
        return { field, kind: "eq", eqValue: expr.eq };
      }
      if (isDerivedDateFieldExpr(expr)) {
        return null;
      }
    }
  }

  const optionValue = resolveValueFromFieldOptions(
    pageData,
    field,
    groupingKey,
  );
  if (optionValue !== null) {
    return { field, kind: "eq", eqValue: optionValue };
  }

  return null;
};

export const buildKanbanPartitionSpecs = (args: {
  blockId: string;
  block: GetPageResponse["blocks"][number];
  pageData?: GetPageResponse;
  sectionConfig?: KanbanSectionConfig;
  subsectionConfig?: KanbanSubsectionConfig;
}): KanbanPartitionSpec[] => {
  const { blockId, block, pageData, sectionConfig, subsectionConfig } = args;

  const expandedSections = expandGroupedSections(
    sectionConfig ? [sectionConfig] : [],
    block,
    pageData,
  );
  const hasConfiguredSection = Boolean(sectionConfig?.field);
  const hasSections = expandedSections.length > 0;
  if (hasConfiguredSection && !hasSections) {
    return [];
  }
  const expandedSubsections = expandGroupedSubsections(
    subsectionConfig ? [subsectionConfig] : [],
    pageData,
  );
  const baseSubsections =
    expandedSubsections.length > 0
      ? expandedSubsections
      : [{ field: "", groupingKey: "", subQuery: undefined }];

  const sections = hasSections
    ? expandedSections
    : [{ field: "", groupingKey: "", subQuery: undefined }];

  const specs: KanbanPartitionSpec[] = [];

  for (const section of sections) {
    const sectionGroup = toKanbanGroupKey({
      field: section.field,
      groupingKey: section.groupingKey,
    });

    const sectionMatch =
      hasSections && section.field
        ? extractEqMatchFromSubQuery(
            section.subQuery,
            section.field,
            pageData,
            section.groupingKey,
          )
        : null;

    for (const sub of baseSubsections) {
      const subsectionGroup = toKanbanGroupKey({
        field: sub.field,
        groupingKey: sub.groupingKey,
      });
      const partition = buildKanbanPartitionKey({
        blockId,
        hasSections,
        section: sectionGroup,
        subsection: subsectionGroup,
      });

      const match: KanbanPartitionMatch[] = [];
      if (sectionMatch) {
        match.push(sectionMatch);
      }

      if (subsectionGroup.field) {
        const subsectionSubQuery = sub.subQuery ?? section.subQuery;
        const subsectionMatch = extractEqMatchFromSubQuery(
          subsectionSubQuery,
          sub.field,
          pageData,
          sub.groupingKey,
        );
        if (subsectionMatch) {
          match.push(subsectionMatch);
        }
      }

      specs.push({ partition, match });
    }
  }

  return specs;
};

const rowValueEquals = (rowValue: unknown, expected: unknown): boolean => {
  if (Array.isArray(rowValue)) {
    return rowValue.some((entry) => rowValueEquals(entry, expected));
  }
  return String(rowValue ?? "") === String(expected ?? "");
};

const isEmptyKanbanRowValue = (rowValue: unknown): boolean => {
  if (rowValue === undefined || rowValue === null) {
    return true;
  }
  if (typeof rowValue === "string") {
    return rowValue.trim().length === 0;
  }
  if (Array.isArray(rowValue)) {
    return rowValue.length === 0;
  }
  return false;
};

export const rowMatchesKanbanPartition = (
  row: Record<string, unknown>,
  match: KanbanPartitionMatch[],
): boolean => {
  if (match.length === 0) {
    return true;
  }

  return match.every((entry) => {
    if (entry.kind === "unmatched") {
      return true;
    }
    if (entry.kind === "empty") {
      return isEmptyKanbanRowValue(row[entry.field]);
    }
    return rowValueEquals(row[entry.field], entry.eqValue);
  });
};

export const partitionKanbanRows = (
  rows: Array<Record<string, unknown>>,
  specs: KanbanPartitionSpec[],
): Map<string, Array<Record<string, unknown>>> => {
  const buckets = new Map<string, Array<Record<string, unknown>>>();
  for (const spec of specs) {
    buckets.set(serializeKanbanPartitionKey(spec.partition), []);
  }

  for (const row of rows) {
    for (const spec of specs) {
      if (rowMatchesKanbanPartition(row, spec.match)) {
        buckets.get(serializeKanbanPartitionKey(spec.partition))?.push(row);
        break;
      }
    }
  }

  return buckets;
};

export const findKanbanSubsectionData = (
  blockData: unknown,
  partition: KanbanPartitionKey,
): Array<Record<string, unknown>> => {
  if (
    !blockData ||
    typeof blockData !== "object" ||
    !("sections" in blockData) ||
    !Array.isArray((blockData as { sections: unknown }).sections)
  ) {
    return [];
  }

  const sections = (blockData as {
    sections: Array<{
      group: KanbanGroupKey;
      subsections: Array<{
        group: KanbanGroupKey;
        data: Array<Record<string, unknown>>;
      }>;
    }>;
  }).sections;

  for (const section of sections) {
    if (!kanbanGroupKeysEqual(section.group, partition.section)) {
      continue;
    }
    for (const subsection of section.subsections) {
      if (kanbanGroupKeysEqual(subsection.group, partition.subsection)) {
        return subsection.data;
      }
    }
  }

  return [];
};

export const findKanbanSubsectionPagination = (
  blockData: unknown,
  partition: KanbanPartitionKey,
) => {
  if (
    !blockData ||
    typeof blockData !== "object" ||
    !("sections" in blockData) ||
    !Array.isArray((blockData as { sections: unknown }).sections)
  ) {
    return undefined;
  }

  const typed = blockData as {
    sections: Array<{
      group: KanbanGroupKey;
      subsections: Array<{
        group: KanbanGroupKey;
        pagination?: {
          page: number;
          total: number;
          hasMore: boolean;
          limit?: number;
        };
      }>;
    }>;
    pagination?: {
      page: number;
      total: number;
      hasMore: boolean;
      limit?: number;
    };
  };

  for (const section of typed.sections) {
    if (!kanbanGroupKeysEqual(section.group, partition.section)) {
      continue;
    }
    for (const subsection of section.subsections) {
      if (kanbanGroupKeysEqual(subsection.group, partition.subsection)) {
        // Subsection-scoped only — page-level pagination is the LIST total.
        return subsection.pagination;
      }
    }
  }

  return undefined;
};
